import { z } from "zod";

/**
 * #1111 (access control review 2026-10-14, owner decision 2026-10-15): a tenant's optional
 * "Allowed notification domains".
 *
 * 1. A Tenant admin (`tenant.instances.manage`) can turn it on for the tenant. It is off by
 *    default: no row is off (platform 0056, `tenant_notification_domains`).
 * 2. While it is on, an address of a notification rule or a rule for chosen connections or
 *    schedules whose domain is not in the list is refused with a 422 that names the field
 *    (`rules.<i>.emails.<j>`), with `OUTSIDE_ALLOWED_DOMAINS_MESSAGE`. An address saved before the
 *    setting was turned on is written `skipped` `outside_allowed_domains` when an alert would go
 *    to it, so turning the setting on takes effect at once (`runner/notifications/expand.ts`).
 * 3. External addresses are marked in the Settings list in every case (`isExternalAddress`): while
 *    the setting is on, an address outside the list; while it is off, an address whose domain no
 *    person with access to the instance signs in with (`RecipientPolicy.accessDomains`).
 *
 * The list holds host names, matched exactly and without case: `contoso.com` admits
 * `ada@contoso.com`, not `ada@eu.contoso.com` (list that one too). It governs the addresses an
 * admin types. It does not govern a Watch or the credential fallback (both go to a person's own
 * sign-in address, checked against a live grant), an invitation email (the grant's own person),
 * the L1 list (Skypoint's) or the Teams webhook (a Microsoft host, never the customer's domain,
 * already bounded by the deployment's egress list; the General tab shows its host instead).
 */

/** A bound, not a product limit: the migration's CHECK holds the same number. */
export const NOTIFICATION_DOMAINS_MAX = 50;

/** The sentence a refused address carries, on the server and on the screen (owner wording). */
export const OUTSIDE_ALLOWED_DOMAINS_MESSAGE =
  "This address is outside the allowed domains for your organization.";

/** The save's sentence when the setting is on with no domain. */
export const DOMAINS_REQUIRED_MESSAGE = "Add at least one domain, or turn the setting off.";

/** What a domain line may be: a host name with at least one dot, letters, digits and hyphens. */
const DOMAIN_PATTERN = /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/;

/** `@Contoso.com ` and `contoso.com` are the same domain. */
export function normaliseDomain(raw: string): string {
  return raw.trim().replace(/^@/, "").toLowerCase();
}

export function isValidDomain(raw: string): boolean {
  return DOMAIN_PATTERN.test(normaliseDomain(raw));
}

const domainSchema = z
  .string()
  .max(260)
  .transform(normaliseDomain)
  .refine((value) => DOMAIN_PATTERN.test(value), "Not a domain name, for example contoso.com.");

/** The domain of an address, lowercased; null when it has none. */
export function addressDomain(address: string): string | null {
  const trimmed = address.trim().toLowerCase();
  const at = trimmed.lastIndexOf("@");
  if (at <= 0 || at === trimmed.length - 1) return null;
  return trimmed.slice(at + 1);
}

/** True when the address's domain is not one of `domains` (already normalised). */
export function isOutsideDomains(address: string, domains: readonly string[]): boolean {
  const domain = addressDomain(address);
  return domain === null || !domains.includes(domain);
}

/** `PUT /tenants/:id/notification-domains`. Deduplicated in first-seen order on the server. */
export const putNotificationDomainsBodySchema = z
  .object({
    enabled: z.boolean(),
    domains: z.array(domainSchema).max(NOTIFICATION_DOMAINS_MAX),
  })
  .strict()
  .refine((body) => !body.enabled || body.domains.length > 0, {
    message: DOMAINS_REQUIRED_MESSAGE,
    path: ["domains"],
  });
export type PutNotificationDomainsBody = z.infer<typeof putNotificationDomainsBodySchema>;

export const notificationDomainsParamsSchema = z.object({ id: z.uuid() }).strict();

/** `GET|PUT /tenants/:id/notification-domains`. */
export const notificationDomainsViewSchema = z.object({
  tenantId: z.uuid(),
  enabled: z.boolean(),
  domains: z.array(z.string()),
  /** Null while the tenant has never saved it (off by default). */
  updatedAt: z.string().nullable(),
  /**
   * The saved rule addresses of the tenant's instances that are outside the list, counted per
   * instance (never the addresses). Empty while the setting is off.
   */
  outside: z.array(
    z.object({ instanceId: z.uuid(), instanceName: z.string(), count: z.number().int() }),
  ),
});
export type NotificationDomainsView = z.infer<typeof notificationDomainsViewSchema>;

/**
 * What the Notifications tab needs to mark and refuse an address, on the rules and scoped rules
 * views (every reader of those views, Instance admin and above).
 */
export const recipientPolicySchema = z.object({
  /** The tenant's setting: on, and its domains. */
  allowedDomains: z.object({ enabled: z.boolean(), domains: z.array(z.string()) }),
  /**
   * The domains people with access to this instance sign in with (instance and tenant grants,
   * never a platform grant): what "External" means while the setting is off.
   */
  accessDomains: z.array(z.string()),
});
export type RecipientPolicy = z.infer<typeof recipientPolicySchema>;

/** A policy that marks nothing and refuses nothing (an older server's view). */
export const OPEN_RECIPIENT_POLICY: RecipientPolicy = {
  allowedDomains: { enabled: false, domains: [] },
  accessDomains: [],
};

/** True when the screen marks the address External (item 3 of the owner decision). */
export function isExternalAddress(address: string, policy: RecipientPolicy): boolean {
  if (policy.allowedDomains.enabled)
    return isOutsideDomains(address, policy.allowedDomains.domains);
  if (policy.accessDomains.length === 0) return false;
  return isOutsideDomains(address, policy.accessDomains);
}

/** True when a save would refuse the address (item 2): only while the setting is on. */
export function isRefusedAddress(address: string, policy: RecipientPolicy): boolean {
  return policy.allowedDomains.enabled && isOutsideDomains(address, policy.allowedDomains.domains);
}
